/*
 * Copyright (c) 2020 Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { Formik, Form, Field } from "formik";
import classNames from "classnames";

// @material-ui/core components
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import {
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
// @material-ui/icons
import { Face, Lock, Mail } from "@material-ui/icons";

// TC UX
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";

import { registerUser } from "./authCognito.jsx";
import { checkAccessCode } from "./accountUtils";
import {
  signupValidation,
  loginSocialButtons,
  FormTextField,
  FormCheckboxField,
  btnFormSubmit,
  passwordEyeIcon,
} from "components/Form/FormField";
import { Modal } from "components/Modal/Modal";
import {
  sCreateUser,
  sSignUpUser2,
  sVerifyMailSent,
  sSignUpFail,
  sNewUserFail,
  sSignUpFailMsg,
  sOrByMail,
  sLogin,
  sBySocial,
  sBack,
  sErrTwoPW,
  sLegal,
  sTerms,
  sPolicy,
  sBySignUp,
} from "assets/jss/views/ResStrings";
import { showHtmlText } from "client/views/Exercise/ExMain";

import { classicLogin, ApricotAction } from "client/actions/user.ts";
import TermOfUse from "client/views/Legal/Content/Terms";
import Policy from "client/views/Legal/Content/Policy";
import { progressCircles } from "components/Form/FormField";

import accountPageStyle from "assets/jss/views/accountPage/accountPageStyle.jsx";
const styles = createStyles(accountPageStyle as any);

interface FormValues {
  email: string;
  name: string;
  newPass1: string;
  newPass2: string;
  agree: boolean;
}

interface Props extends WithStyles<typeof styles> {
  classicLogin: ApricotAction;
  onExit: () => void;
  onLogin: () => void;
}

const SignupDialogBase: React.FC<Props> = ({
  // actions
  classicLogin,
  // component props
  onExit,
  onLogin,
  classes,
}: Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  const [signupStart, setSignupStart] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertModal, setAlertModal] = useState<React.ReactElement | null>(null);

  const ref = React.createRef();

  const showMessage = (
    title: string,
    body: string,
    warning: boolean = false,
    func: () => void = () => {},
  ) => {
    setAlertModal(
      <Modal
        title={title}
        color={warning ? "warning" : "secondary"}
        onExit={() => {
          setShowAlert(false);
          func && func();
        }}>
        {showHtmlText(body)}
      </Modal>,
    );
    setShowAlert(true);
  };

  const singupUser = async (email: string, password: string, name: string) => {
    setSignupStart(true);
    var res = await registerUser(email, password, name);

    if (res.result) {
      // register success, two things:
      // 1. write to our DB (this is the only chance we can keep the "name").
      classicLogin(email, name);
      setSignupStart(false);

      // 2. tell user the verfication link is sent.
      showMessage(sSignUpUser2, sVerifyMailSent, false, onExit);
    } else {
      setSignupStart(false);
      showMessage(
        sSignUpFail,
        res.err.code === "UsernameExistsException"
          ? sNewUserFail
          : sSignUpFailMsg,
        true,
      );
    }
  };

  const renderPasswordField = (valueName: string, labelPrefix: string) => (
    <Field
      name={valueName}
      type={showPassword ? "text" : "password"}
      icon={<Lock />}
      component={FormTextField}
      label={labelPrefix + "Password"}
      eye={passwordEyeIcon(showPassword, () => setShowPassword(!showPassword))}
    />
  );

  const renderSignUpInput = () => (
    <>
      <Field
        name="name"
        type="text"
        icon={<Face />}
        component={FormTextField}
        label="Name"
      />
      <Field
        name="email"
        type="text"
        icon={<Mail />}
        component={FormTextField}
        label="Email"
      />

      {renderPasswordField("newPass1", "")}
      {renderPasswordField("newPass2", "Retype ")}

      <>
        <Field
          name="agree"
          type="checkbox"
          component={FormCheckboxField}
          label={sBySignUp}
        />

        <div style={{ marginTop: "-10px", textAlign: "center" }}>
          <span
            className={classes.legalNote}
            onClick={() => setShowTerms(true)}>
            {sTerms}
          </span>
          &nbsp;&amp;&nbsp;
          <span
            className={classes.legalNote}
            onClick={() => setShowPolicy(true)}>
            {sPolicy}
          </span>
        </div>
      </>
    </>
  );

  const handleSubmit = (values: FormValues) => {
    const { email, name, newPass1, newPass2 } = values;
    if (newPass1 !== newPass2) {
      showMessage(sSignUpFail, sErrTwoPW, true);
    } else {
      singupUser(email, newPass1, name);
    }
  };

  const closeDlg = () => onExit();

  const chkCode = checkAccessCode(closeDlg);
  if (chkCode.passed === false) {
    return chkCode.alertModal;
  }

  return (
    <>
      {showAlert && alertModal}
      {signupStart && progressCircles()}
      {showTerms && (
        <Modal
          wide
          title={sLegal}
          buttons={[sBack]}
          onExit={() => setShowTerms(false)}>
          <TermOfUse
            onShowPolicy={() => {
              setShowPolicy(true);
            }}
          />
        </Modal>
      )}
      {showPolicy && (
        <Modal
          wide
          title={sLegal}
          buttons={[sBack]}
          onExit={() => setShowPolicy(false)}>
          <Policy />
        </Modal>
      )}
      <Dialog
        classes={{
          root: classes.modalRoot,
          paper: classes.modal + " " + classes.modalLogin,
        }}
        open={true}
        fullWidth={true}
        ref={ref}
        onClose={closeDlg}>
        <Card plain className={classes.modalLoginCard}>
          <DialogTitle disableTypography className={classes.modalHeader}>
            <CardHeader plain signup>
              <h4
                style={{ fontWeight: 700 }}
                className={classes.cardTitleWhite}>
                {sCreateUser}
              </h4>
              <h5
                style={{ fontWeight: 300 }}
                className={classes.cardTitleWhite}>
                {sBySocial}
              </h5>
              <div className={classes.socialLine}>
                {loginSocialButtons(classes)}
              </div>
            </CardHeader>
          </DialogTitle>
          <DialogContent className={classes.modalBody}>
            <h5 style={{ textAlign: "center", marginBottom: "0" }}>
              {sOrByMail}
            </h5>
            <CardBody className={classes.cardLoginBody}>
              <Formik
                initialValues={{
                  email: "",
                  newPass1: "",
                  newPass2: "",
                  name: "",
                  agree: false,
                }}
                onSubmit={(values: FormValues) => handleSubmit(values)}
                validationSchema={signupValidation}
                render={({ isValid }) => (
                  <Form style={{ marginBottom: "0.5rem" }}>
                    {renderSignUpInput()}
                    <DialogActions
                      className={classNames(
                        classes.modalFooter,
                        classes.justifyContentCenter,
                      )}>
                      {btnFormSubmit({
                        label: sSignUpUser2,
                        disabled: !isValid || signupStart,
                        color: "primary",
                      })}
                    </DialogActions>
                  </Form>
                )}
              />
              <Divider variant="fullWidth" />
              <div
                style={{
                  textAlign: "center",
                  margin: "0.5rem auto 1rem auto",
                }}>
                <span style={{ display: "inline-block" }}>
                  Already signed up?
                  <span
                    className={classes.loginLink}
                    onClick={() => {
                      closeDlg();
                      onLogin();
                    }}>
                    &nbsp;&nbsp;{sLogin}
                  </span>
                </span>
              </div>
            </CardBody>
          </DialogContent>
        </Card>
      </Dialog>
    </>
  );
};

const base: any = SignupDialogBase;
export const SignUpDialog: any = withStyles(styles)(
  connect(null, { classicLogin })(base),
);

export const SignUpDialogTest = withStyles(styles)(base);
